const article = {
  slug: "days-on-market-explained",
  cluster: "seller",
  title: "What Is Days on Market (DOM) and When Is It Too Long?",
  description:
    'Days on market (DOM) explained: what it is, what counts toward it, why a high DOM scares off buyers, what "too long" means locally, and how to reset it.',
  audience: "Sellers & agents",
  funnelStage: "TOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "6 min read",
  tldr: 'Days on market (DOM) counts how many days a listing has been active and for sale. It matters because once DOM climbs past your local norm, buyers assume something is wrong and start expecting a discount. "Too long" is relative to your market\'s typical pace, but the fix is almost always price, presentation, or a clean relist.',
  blocks: [
    {
      type: "p",
      spans: [
        'Days on market (DOM) is the number of days a home has been listed and actively for sale, from the day it hits the market until it goes under contract (or sells, depending on how it\'s measured). It\'s the single most-watched number on a listing after price, because buyers and their agents read it as a signal: a low DOM says "fresh and in demand," while a high DOM whispers "something might be wrong here."',
      ],
    },
    {
      type: "callout",
      spans: [
        "DOM is a clock that starts the moment your listing goes active. Every day it ticks up, your negotiating power quietly ticks down.",
      ],
    },
    {
      type: "h2",
      text: "What exactly is days on market?",
      id: "what-is-days-on-market",
    },
    {
      type: "p",
      spans: [
        'DOM measures how long your specific listing has been actively for sale on the MLS. The counter starts when the listing status flips to "Active" and stops when it goes "Pending" or "Under Contract." The day it goes live is typically day zero or day one, and it counts up from there. A home listed three weeks ago that\'s still available shows a DOM of roughly 21.',
      ],
    },
    {
      type: "p",
      spans: [
        "You'll also see a related figure called Cumulative Days on Market (CDOM). Regular DOM can reset to zero if a listing is withdrawn and relisted, but CDOM follows the property across those relistings and shows the true total. Savvy buyers' agents look at CDOM specifically to catch homes that have been quietly sitting longer than the headline number suggests. For a deeper breakdown of how the clock behaves, see ",
        {
          text: "days on market explained",
          href: "/resources/days-on-market-explained",
        },
        ".",
      ],
    },
    {
      type: "h2",
      text: "What counts toward DOM (and what doesn't)?",
      id: "what-counts-toward-dom",
    },
    {
      type: "p",
      spans: [
        "Not every day your home is off the market counts, and the rules can be surprisingly easy to game by accident. Here's the general logic most MLS systems follow:",
      ],
    },
    {
      type: "table",
      headers: ["Counts toward DOM", "Usually does NOT count"],
      rows: [
        ["Days the listing is Active and for sale", "Days the listing is Pending / Under Contract"],
        [
          "Time before any price change (price drops don't reset DOM)",
          "Time while withdrawn or in 'Temporarily Off Market' status",
        ],
        [
          "Re-activated days after a buyer falls through",
          "Days before the listing ever went live (prep, photos, staging)",
        ],
        [
          "Original active days, tracked in CDOM even after a relist",
          "A brand-new DOM count after a long enough off-market gap (varies by MLS)",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        'Two things trip sellers up most. First, a price reduction does not reset DOM — the clock keeps running. Second, pulling a listing and immediately relisting it to "start fresh" only resets the visible DOM; CDOM still tells the real story, and most agents know to check it. If you\'re considering that move, read ',
        {
          text: "how to relist a house",
          href: "/resources/how-to-relist-a-house",
        },
        " before you do anything.",
      ],
    },
    {
      type: "h2",
      text: "Why is a high days on market its own problem?",
      id: "why-high-dom-is-a-problem",
    },
    {
      type: "p",
      spans: [
        "This is the part sellers underestimate. A high DOM doesn't just reflect a problem — it becomes one. The number itself changes how buyers behave, independent of the actual home.",
      ],
    },
    {
      type: "ul",
      items: [
        [
          "Buyers assume something is wrong. When a home sits while others sell, the natural conclusion is a hidden defect, a bad layout, or an inflated price — even when the only real issue was a weak first impression.",
        ],
        [
          "It invites lowball offers. A long DOM signals a motivated (or desperate) seller, so buyers anchor their offers below asking and negotiate harder.",
        ],
        [
          "It compounds. The longer it sits, the higher the DOM, the more suspicious the next buyer is — a feedback loop that's hard to break without a decisive change.",
        ],
        [
          "It shrinks your buyer pool. The most active, ready-to-move buyers see your listing first. Once they pass, you're left fishing in a thinner pond of later, more skeptical shoppers.",
        ],
      ],
    },
    {
      type: "stat",
      value: "Most homes that sell do so in their first few weeks on the market",
      label:
        "General industry pattern — the early window, when buyer attention is highest, is when serious offers are most likely to arrive.",
    },
    {
      type: "p",
      spans: [
        "Because that early window matters so much, the cost of a weak launch is real: a listing that's mispriced or poorly presented in week one can carry that disadvantage for months. The goal is to get it right before the clock starts, not to fix it after.",
      ],
    },
    {
      type: "h2",
      text: "When is days on market 'too long'?",
      id: "when-is-dom-too-long",
    },
    {
      type: "p",
      spans: [
        '"Too long" is always relative to your local market, not a universal number. The reference point is your area\'s median DOM for comparable homes — same price range, same neighborhood, same property type. In a fast market homes may go pending within a week or two, so two months would be a glaring outlier. In a slower market, that same two months might be completely normal.',
      ],
    },
    {
      type: "p",
      spans: [
        "A practical rule of thumb: once your listing crosses roughly the local median DOM with no accepted offer, treat it as a yellow flag and review. Once you're at one and a half to two times the local median, buyers are actively reading your listing as stale, and it's time for a decisive move — not another small price nudge. Your agent can pull the exact local median from the MLS; a ",
        {
          text: "comparative market analysis (CMA)",
          href: "/resources/what-is-a-cma",
        },
        " is the cleanest way to see where your home actually sits.",
      ],
    },
    {
      type: "callout",
      spans: [
        "There's no magic universal DOM threshold. 'Too long' means meaningfully longer than comparable homes in your own market are taking to go under contract.",
      ],
    },
    {
      type: "h2",
      text: "How do you avoid — or reset — a high DOM?",
      id: "how-to-avoid-or-reset-dom",
    },
    {
      type: "p",
      spans: [
        "The best DOM strategy is to never need one: launch right. But if your listing is already sitting, a thoughtful reset can give it a genuine second chance instead of a cosmetic one.",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Diagnose before you discount. Lots of views but no showings usually means the price or photos are off; showings but no offers usually means condition, layout, or price-versus-condition. Pinpoint the real bottleneck first — see ",
          {
            text: "views but no showings",
            href: "/resources/views-but-no-showings",
          },
          ".",
        ],
        [
          "Fix the price to the market, not the wish. If you're well above comparable solds, a single meaningful correction beats a series of timid drops that just train buyers to wait for the next one.",
        ],
        [
          "Rework the presentation. Lead with the strongest photo, reorder the gallery, and rewrite a tired description so the listing reads fresh and specific instead of generic.",
        ],
        [
          "Reset deliberately, not deceptively. If you withdraw and relist, pair it with real changes — new price, new photos, new copy — so the lower DOM reflects a genuinely improved listing, not a trick. Buyers' agents see CDOM anyway.",
        ],
        [
          "Get an objective second read. Run the listing through an outside assessment of price position, appeal, and competitiveness before you relaunch so you're fixing the actual problem.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "If your home has stalled and you're not sure why, a ",
        {
          text: "Listing Review",
          href: "/get-started",
        },
        " gives you an at-a-glance read on market competitiveness, listing appeal, and price position, plus a rewritten description and photo sequencing — exactly the inputs that drive DOM. And if you haven't listed yet, getting the launch right the first time is what ",
        {
          text: "Pre-Listing Prep",
          href: "/resources/listing-review-vs-pre-listing-prep",
        },
        " is built for. Still stuck on the bigger picture? Start with ",
        {
          text: "why isn't my house selling",
          href: "/resources/why-isnt-my-house-selling",
        },
        ".",
      ],
    },
  ],
  faqs: [
    {
      question: "What does days on market actually mean?",
      answer: [
        {
          type: "p",
          spans: [
            "Days on market (DOM) is the number of days a home has been actively listed and for sale, counting from the day the listing goes live until it goes under contract or pending. It's a core indicator of how a listing is performing: a low DOM signals strong demand, while a high DOM tends to make buyers suspect a problem with the price or the property.",
          ],
        },
      ],
    },
    {
      question: "Does a price reduction reset days on market?",
      answer: [
        {
          type: "p",
          spans: [
            "No. A price reduction does not reset DOM in most MLS systems — the clock keeps running. Only withdrawing the listing and relisting it (sometimes after a required off-market period) resets the visible DOM, and even then Cumulative Days on Market (CDOM) still tracks the property's full history, which buyers' agents routinely check.",
          ],
        },
      ],
    },
    {
      question: "How many days on market is too long?",
      answer: [
        {
          type: "p",
          spans: [
            "There's no universal number — \"too long\" is relative to your local market. Compare your listing to the median DOM for similar homes in your area. Crossing the local median with no offer is a yellow flag; reaching roughly one and a half to two times the local median means buyers are reading your listing as stale and it's time for a decisive change, not another small price nudge.",
          ],
        },
      ],
    },
    {
      question: "Why do buyers care so much about days on market?",
      answer: [
        {
          type: "p",
          spans: [
            "Because DOM is a proxy for desirability. When a home sits longer than comparable listings, buyers assume there's a hidden flaw or that it's overpriced, so they anchor lower and negotiate harder. A high DOM also signals a motivated seller, which invites lowball offers. The number itself shapes buyer behavior, independent of the home's actual condition.",
          ],
        },
      ],
    },
    {
      question: "Can I reset days on market by relisting my house?",
      answer: [
        {
          type: "p",
          spans: [
            "Technically yes — withdrawing and relisting can zero out the visible DOM — but it rarely works as a standalone trick because Cumulative Days on Market still shows the true total, and experienced buyers' agents look for it. A relist only helps when it's paired with real improvements: a corrected price, better photos, and a rewritten description that gives the listing a genuine fresh start.",
          ],
        },
      ],
    },
  ],
  related: [
    "how-to-relist-a-house",
    "what-is-a-cma",
    "views-but-no-showings",
    "listing-review-vs-pre-listing-prep",
  ],
};

export default article;
